/**
 * h5/src/admin/unlockStateMachine.ts
 * F.6 — Admin 解锁状态机（4 态）+ PIN Keychain 覆盖
 *
 * 状态图：
 *   IDLE ──(5连击触发)──> CHALLENGING ──(PIN+attest通过)──> UNLOCKED ──(5min TTL/手动)──> IDLE
 *                              ↓ (fail/cancel)
 *                            IDLE
 *
 * R4 五层防护：
 *   L1: 路由动态注册/卸载（IDLE 时 hasRoute===false）
 *   L3: 5min TTL 自动锁回
 *   L4: prod 不暴露 window.__cardshopUnlock
 */

import { ref, readonly } from 'vue'
import type { Ref } from 'vue'
import { pushAudit } from './auditLog'
import { isTauri } from '@/utils/tauri-bridge'

// ─── 类型定义 ────────────────────────────────────────────────────────────────

export type UnlockState = 'IDLE' | 'CHALLENGING' | 'UNLOCKED'

export interface UnlockStateMachine {
  /** 当前状态（只读） */
  readonly state: Readonly<Ref<UnlockState>>
  /** 触发挑战（5 连击后调用） */
  trigger(): void
  /** 挑战成功 → UNLOCKED */
  onChallengePass(): void
  /** 挑战失败/取消 → IDLE */
  onChallengeFail(): void
  /** 手动锁定 → IDLE */
  lock(): void
  /** 获取剩余 TTL 秒数（仅 UNLOCKED 时有效，否则返回 0） */
  remainingTtlSecs(): number
}

// ─── 常量 ────────────────────────────────────────────────────────────────────

export const UNLOCK_TTL_MS = 300_000        // 5 分钟
export const PIN_FAIL_MAX = 5              // 最大连续失败次数
export const PIN_COOLDOWN_MS = 300_000     // 5 分钟冷却

// ─── 内部状态（模块级单例，便于测试 reset） ───────────────────────────────────

let _state: Ref<UnlockState> = ref<UnlockState>('IDLE')
let _ttlTimer: ReturnType<typeof setTimeout> | null = null
let _unlockAt: number | null = null

// PIN 连续失败计数
let _pinFailCount = 0
let _pinCooldownUntil = 0

// 路由实例（由 initUnlockMachine 注入）
let _router: {
  hasRoute(name: string): boolean
  addRoute(record: { path: string; name: string; component: () => Promise<{ default: unknown }> }): void
  removeRoute(name: string): void
  push(location: { name: string }): void
} | null = null

// ─── 路由挂载/卸载（L1 防护） ────────────────────────────────────────────────

const ADMIN_ROUTE_NAME = 'admin-settings'
const ADMIN_ROUTE_PATH = '/_admin/settings'

function _mountRoute(): void {
  if (!_router) return
  if (_router.hasRoute(ADMIN_ROUTE_NAME)) return
  _router.addRoute({
    path: ADMIN_ROUTE_PATH,
    name: ADMIN_ROUTE_NAME,
    component: () =>
      import('@/pages/admin/SettingsPage.vue') as Promise<{ default: unknown }>,
  })
}

function _unmountRoute(): void {
  if (!_router) return
  if (_router.hasRoute(ADMIN_ROUTE_NAME)) {
    _router.removeRoute(ADMIN_ROUTE_NAME)
  }
}

// ─── TTL 管理（L3 防护） ─────────────────────────────────────────────────────

function _startTtl(): void {
  _clearTtl()
  _unlockAt = Date.now()
  _ttlTimer = setTimeout(() => {
    _ttlTimer = null
    _doLock('auto', 'ttl_expired')
  }, UNLOCK_TTL_MS)
}

function _clearTtl(): void {
  if (_ttlTimer !== null) {
    clearTimeout(_ttlTimer)
    _ttlTimer = null
  }
  _unlockAt = null
}

// ─── 状态转换核心 ─────────────────────────────────────────────────────────────

function _doLock(actor: 'admin' | 'system' | 'auto', event: 'lock' | 'ttl_expired'): void {
  _clearTtl()
  _unmountRoute()
  _state.value = 'IDLE'
  pushAudit(event === 'ttl_expired' ? 'ttl_expired' : 'lock', actor, {
    previous_state: 'UNLOCKED',
  })

  // 若当前在 admin 页则跳回首页
  if (_router && typeof window !== 'undefined') {
    try {
      if (window.location.pathname.startsWith('/_admin')) {
        _router.push({ name: 'home' })
      }
    } catch {
      // ignore
    }
  }
}

// ─── 主状态机工厂 ─────────────────────────────────────────────────────────────

function _createMachine(): UnlockStateMachine {
  return {
    state: readonly(_state),

    trigger() {
      if (_state.value !== 'IDLE') return
      // 检查 PIN 冷却
      if (_pinCooldownUntil > Date.now()) {
        pushAudit('pin_cooldown', 'system', {
          cooldown_until: _pinCooldownUntil,
        })
        return
      }
      _state.value = 'CHALLENGING'
    },

    onChallengePass() {
      if (_state.value !== 'CHALLENGING') return
      _pinFailCount = 0
      _state.value = 'UNLOCKED'
      _mountRoute()
      _startTtl()
      pushAudit('unlock', 'admin')
    },

    onChallengeFail() {
      if (_state.value !== 'CHALLENGING') return
      _pinFailCount++
      pushAudit('factor_fail', 'admin', { fail_count: _pinFailCount })

      if (_pinFailCount >= PIN_FAIL_MAX) {
        _pinCooldownUntil = Date.now() + PIN_COOLDOWN_MS
        _pinFailCount = 0
        pushAudit('pin_cooldown', 'system', {
          cooldown_until: _pinCooldownUntil,
        })
      }
      _state.value = 'IDLE'
    },

    lock() {
      if (_state.value !== 'UNLOCKED') return
      _doLock('admin', 'lock')
    },

    remainingTtlSecs() {
      if (_state.value !== 'UNLOCKED' || _unlockAt === null) return 0
      const elapsed = Date.now() - _unlockAt
      const remaining = UNLOCK_TTL_MS - elapsed
      return Math.max(0, Math.floor(remaining / 1000))
    },
  }
}

// ─── 单例持有 ─────────────────────────────────────────────────────────────────

let _machine: UnlockStateMachine | null = null

/**
 * 获取解锁状态机单例
 * 首次调用前必须先 initUnlockMachine(router) 注入路由实例
 */
export function getUnlockMachine(): UnlockStateMachine {
  if (!_machine) {
    _machine = _createMachine()
  }
  return _machine
}

/**
 * 注入路由并初始化状态机（在 main.ts 或 App.vue 调用一次）
 */
export function initUnlockMachine(router: typeof _router): void {
  _router = router
  if (!_machine) {
    _machine = _createMachine()
  }
  // L4：prod 不暴露 window.__cardshopUnlock
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).__cardshopUnlock = _machine
  }
  // 确保初始状态路由已卸载（L1 防护）
  _unmountRoute()
}

// ─── PIN 校验（L2 防护） ──────────────────────────────────────────────────────

/**
 * 计算字符串的 SHA-256 hex
 */
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * 校验 PIN 的 SHA-256 hash
 *
 * 优先级：
 *   1. Tauri Keychain（get_admin_pin_hash 返回非空 64 位 hex）
 *   2. 编译期 VITE_ADMIN_PIN_HASH
 *   3. DEV 模式：任意 6 位数字通过
 */
export async function verifyPin(pin: string): Promise<boolean> {
  // 1. 优先从 Tauri Keychain 读取覆盖 PIN 哈希
  if (isTauri()) {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const keychainHash = await invoke<string>('get_admin_pin_hash').catch(() => '')
      if (keychainHash && keychainHash.length === 64) {
        const inputHash = await sha256(pin)
        return inputHash.toLowerCase() === keychainHash.toLowerCase()
      }
    } catch {
      // Keychain 读取失败，降级到编译期哈希
    }
  }

  // 2. 编译期哈希
  const expectedHash = import.meta.env.VITE_ADMIN_PIN_HASH as string | undefined
  if (!expectedHash || expectedHash.length !== 64) {
    // 未配置 PIN hash，仅在 DEV 模式允许任意 6~20 位数字通过
    if (import.meta.env.DEV && /^\d{6,20}$/.test(pin)) {
      return true
    }
    return false
  }

  try {
    const hashHex = await sha256(pin)
    return hashHex.toLowerCase() === expectedHash.toLowerCase()
  } catch {
    return false
  }
}

/**
 * 检查 PIN 是否在冷却期
 */
export function isPinInCooldown(): boolean {
  return _pinCooldownUntil > Date.now()
}

/**
 * 获取 PIN 冷却剩余秒数
 */
export function pinCooldownRemainingSecs(): number {
  if (!isPinInCooldown()) return 0
  return Math.max(0, Math.ceil((_pinCooldownUntil - Date.now()) / 1000))
}

// ─── 测试辅助（仅测试环境使用） ──────────────────────────────────────────────

/**
 * 重置状态机到初始状态
 * @internal 仅供测试使用
 */
export function _resetMachineForTest(): void {
  _clearTtl()
  _unmountRoute()
  _state.value = 'IDLE'
  _pinFailCount = 0
  _pinCooldownUntil = 0
  _machine = null
  _router = null
}

/**
 * 注入 mock 路由（仅供测试使用）
 * @internal
 */
export function _injectRouterForTest(mockRouter: typeof _router): void {
  _router = mockRouter
  if (!_machine) {
    _machine = _createMachine()
  }
}

/**
 * 获取当前 PIN 失败计数（仅供测试）
 * @internal
 */
export function _getPinFailCountForTest(): number {
  return _pinFailCount
}

/**
 * 设置 PIN 冷却截止时间（仅供测试）
 * @internal
 */
export function _setPinCooldownForTest(until: number): void {
  _pinCooldownUntil = until
}
